import { TableCell } from '@/components/Datatable/TableCell';
import { IDatatableData } from '@/type/listing.types';
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { Checkbox } from '@/components/Controls/Checkbox';
import { TableHeaderActions } from '@/components/Custom/Datatable/TableHeaderActions';
import { useSelectedTableItems } from '@/hooks/selected-table-items.hooks';
import { CellContentWithRedirect } from '@/components/Custom/Datatable/CellContentWithRedirect';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import { Switch } from '@/components/Controls/Switch';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import { showModal } from '@/utils/modal.utils';
import { useRequestVehicules } from '@/hooks/vehicule.hooks';
import Loader from '@/components/Loading/Loader';
import { StatusTag } from '@/components/Tag/StatusTag';
// import { formatDateWithLetterMonth } from '@/utils/date.utils';
import { Trans } from 'react-i18next';
import { SmallInfoContent } from '@/components/SmallInfoContent';
import { VehiculeOwnerType } from '@/api/entities/vehicule';
import { useUserRole } from '@/hooks/auth.hooks';
import { VehiculeCheckProgressStatus } from '@/api/entities/vehicule-check';

export const VehiculeTableData = <TSchema extends FieldValues>({
  t,
  data,
  pendingDeactivate,
  onDeactivate,
  pendingDelete,
  onDelete,
}: IDatatableData<TSchema>) => {
  const navigate = useNavigate();
  const { isAdmin } = useUserRole();

  const {
    numberOfItemsText,
    selectedTableData,
    onSelectedItem,
    handleDeactivate,
    handleDelete,
  } = useSelectedTableItems({
    t,
    data,
    onDeactivate,
    onDelete,
  });

  const {
    activateAllVehiculeRequests,
    requestedVehicules,
    isPendingVehicules,
    setActivateAllVehiculeRequests,
    handleModalConfirmRequestVehicules,
    handleRequestVehicule,
  } = useRequestVehicules<TSchema>({
    data,
  });

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <>
      {selectedTableData?.length > 0 && (
        <TableHeaderActions
          colSpan={9}
          text={numberOfItemsText}
          disableDeactivate={selectedTableData?.every((item) => !item?.active)}
          disableDownload
          switchActive={activateAllVehiculeRequests}
          onDeactivate={handleDeactivate}
          onDelete={isAdmin ? handleDelete : undefined}
          onDownload={() => {}}
          onSwitchChange={() => {
            setActivateAllVehiculeRequests((prevState) => !prevState);
            showModal('#request-vehicules-confirm-modal');
          }}
        />
      )}
      {data.map((entry: TSchema) => {
        const totalVehiculeChecks = entry?.vehiculeChecks?.filter((item) =>
          item?.progressStatus?.includes(VehiculeCheckProgressStatus.COMPLETED),
        )?.length;

        return (
          <tr
            key={entry?.id}
            className={twMerge('', !entry?.active && 'tr-deactivated')}
          >
            <TableCell
              tdClassName="sticky-select-column"
              content={
                <Checkbox
                  checked={Boolean(
                    selectedTableData?.find((item) => item?.id === entry?.id),
                  )}
                  onChange={() => onSelectedItem(entry?.id)}
                />
              }
            />
            <TableCell
              content={
                <>
                  <div className="flex gap-2">
                    <StatusTag
                      text="123"
                      className="self-start px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                    <div className="mt-[0.5px] font-medium">
                      {entry?.licensePlateNumber || '--'}
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <StatusTag
                      text="VIN"
                      className="self-start px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                    <div className="mt-[0.5px] break-all">
                      {entry?.vin || '--'}
                    </div>
                  </div>
                </>
              }
            />
            {/* <TableCell content={entry?.brand} />
            <TableCell content={entry?.model} /> */}
            <TableCell
              content={
                <div className="flex flex-col gap-0.75">
                  {entry?.brand && (
                    <div>
                      <span className="font-medium">{t('common:brand')} :</span>{' '}
                      {entry?.brand}
                    </div>
                  )}
                  {entry?.model && (
                    <div>
                      <span className="font-medium">{t('common:model')} :</span>{' '}
                      {entry?.model}
                    </div>
                  )}
                </div>
              }
            />
            <TableCell
              tdClassName="text-center"
              content={entry?.yearOfFirstRegistration || '--'}
            />
            <TableCell
              content={
                <Switch
                  containerClassName="items-center"
                  checked={
                    requestedVehicules?.find((item) => item?.id === entry?.id)
                      ?.requestVehiculeCheck
                  }
                  onChange={() => handleRequestVehicule(entry)}
                />
              }
            />
            {/* <TableCell
              content={
                <div className="flex flex-col gap-1">
                  {entry?.vehiculeChecks[totalVehiculeChecks - 1]
                    ?.createdAt && (
                    <div>
                      {formatDateWithLetterMonth({
                        dateString:
                          entry?.vehiculeChecks[totalVehiculeChecks - 1]
                            ?.createdAt,
                      })}
                    </div>
                  )}
                  <div className="flex gap-2">
                    {entry?.mileage && (
                      <StatusTag
                        text={`${entry?.mileage} km`}
                        className="text-left px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                      />
                    )}
                    <StatusTag
                      text="0 €"
                      className="text-left px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                  </div>
                </div>
              }
            /> */}
            <TableCell
              content={
                entry?.ownerType && entry?.ownerName ? (
                  <SmallInfoContent
                    labelClassName="text-black"
                    label={
                      entry?.ownerType === VehiculeOwnerType.GROUP
                        ? t('common:group')
                        : t('common:company')
                    }
                    value={entry?.ownerName}
                  />
                ) : (
                  <div className="text-center font-medium">--</div>
                )
              }
            />
            <TableCell
              tdClassName="text-center"
              content={
                <StatusTag
                  text={`${
                    entry?.active ? t('common:active') : t('common:inactive')
                  }`}
                  className={twMerge(
                    'pt-[6px] pb-[5px] text-[10.1px] font-semibold [word-spacing:1.9px] leading-snug uppercase',
                    entry?.active &&
                      'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4',
                    !entry?.active &&
                      'bg-red-FFEEF4 border-red-FFCAD6 text-red-FF3680',
                  )}
                />
              }
            />
            <TableCell
              content={
                <CellContentWithRedirect
                  text={totalVehiculeChecks}
                  disabled={!totalVehiculeChecks}
                  onClick={() =>
                    // navigate(`/vehicule-checks/vehicule/${entry?.id}`)
                    navigate(`/vehicule/edit/${entry?.id}?tab=vehiculeChecks`)
                  }
                />
              }
            />
            <TableCell
              tdClassName="sticky-action-column"
              content={
                <TableMenu>
                  <SubMenuItem
                    textClassName="leading-tight"
                    text={
                      <Trans i18nKey={t('requestInspectionToIndividual')} />
                    }
                    leftIconContainerClassName="-mt-3"
                    leftIconClassName="ki-plus-squared !text-[17.5px]"
                    to={`/vehicule-check-request/create/vehicule/${entry?.id}`}
                  />
                  <SubMenuItem
                    text={t('common:edit')}
                    leftIconClassName="ki-notepad-edit !text-[16px]"
                    to={`/vehicule/edit/${entry?.id}`}
                  />
                  <SubMenuItem
                    text={t('common:deactivate')}
                    disabled={!entry?.active || pendingDeactivate}
                    leftIconClassName="ki-cross !text-[19px]"
                    textClassName="line-clamp-1"
                    onClick={() => onDeactivate([entry])}
                  />
                  {isAdmin && (
                    <SubMenuItem
                      text={t('common:delete')}
                      disabled={pendingDelete}
                      leftIconClassName="ki-trash !text-[19px]"
                      textClassName="line-clamp-1"
                      onClick={() => onDelete([entry])}
                    />
                  )}
                </TableMenu>
              }
            />
          </tr>
        );
      })}
      <tr>
        <td>
          <ConfirmationModal
            id="request-vehicules-confirm-modal"
            title={t('common:confirmation')}
            description={
              <span className="text-md text-black">
                {activateAllVehiculeRequests
                  ? t('areYouSureToRequestAllCarInspections')
                  : t('areYouSureToUnrequestAllCarInspections')}
              </span>
            }
            onClose={() => setActivateAllVehiculeRequests(false)}
            onCancel={() => setActivateAllVehiculeRequests(false)}
            onConfirmLabel={t('common:confirm')}
            onConfirm={handleModalConfirmRequestVehicules}
          />
          {isPendingVehicules && <Loader />}
        </td>
      </tr>
    </>
  );
};
